<template>
	<v-card class="event-card elevation-0 d-flex flex-column">
		<div class="event-card__container">
			<div class="event-card__outer">
				<div class="d-flex event-card__list pl-0 pr-0">
					<Banner v-if="banner && !isLoading" :banner="banner" />
					<EventDescription class="mt-5" :event="event" />
					<div class="event-card__section">
						<v-card-text class="event-card__section-title"> Details</v-card-text>
						<div class="d-flex align-center">
							<Icon color="#697583" type="grid" :size="15" :pr="8" />
							<v-card-text class="event-card__subtitle-category">
								{{ event.category.category_name }}
							</v-card-text>
						</div>
						<v-card-text
							v-if="this.description"
							class="event-card__description mt-3"
							v-html="this.description"
						/>
					</div>
					<div class="event-card__section" v-if="attendees.length">
						<v-card-text class="event-card__section-title"> Attendees</v-card-text>

						<div class="d-flex flex-wrap justify-space-between">
							<div
								v-for="(user, index) in attendees.slice(0, 5)"
								:key="index"
								@click="handleClickAttendee(user.user_id)"
							>
								<Avatar
									class="post-preview-card__avatar mr-2 mb-1"
									:size="60"
									:avatar="user.user_logo"
								/>
							</div>

							<div
								v-if="attendees.length > 6"
								class="event-card__attendees-last-container"
							>
								<Avatar
									class="post-preview-card__avatar mr-2 mb-1"
									:size="60"
									:avatar="attendees[5].user_logo"
								/>
								<div class="event-card__attendees-last-member">
									+{{ totalCountAttendees - 6 }}
								</div>
							</div>
						</div>
						<div class="event-card__show-btn" @click="handleClickAttendees">
							<router-link
								:to="{
									name: 'attendees',
									params: { id: this.event.event_id },
								}"
							>
								Show All
							</router-link>
						</div>
					</div>
					<div
						v-if="this.event.eventActions && event.eventActions.length > 0"
						class="event-card__section mb-2"
					>
						<div>
							<v-card-text class="event-card__section-title">
								Event Actions
							</v-card-text>

							<div class="d-flex flex-wrap">
								<ActionPreview
									v-for="(action, index) in this.event.eventActions"
									:key="index"
									class="event-card__event-wrapper"
									:action="action"
									@click.native="handleEventActionClick(action)"
								>
									<div
										class="event-card__action-wrapper"
										v-bind:style="{ background: getAction(action).color }"
									>
										<Icon
											:type="getAction(action).icon"
											color="white"
											:size="16"
										/>
									</div>
									<div class="event-card__event-title text-decoration-none">
										{{ getAction(action).name }}
									</div>
								</ActionPreview>
							</div>
						</div>
					</div>
					<div
						class="event-card__section"
						v-if="partners && partners.length > 0"
						v-for="(
							{ group_name = '', partners = [], group_id = 0, partner_per_group = 6 },
							index
						) in this.event.partner_groups"
						:key="index"
					>
						<v-card-text class="event-card__section-title">
							{{ group_name }}
						</v-card-text>

						<div class="event-card__exhibitors-container">
							<div
								v-for="(partner, index) in partners.slice(0, partner_per_group)"
								:key="index"
								class="event-card__exhibitors"
								@click="handleExhibitorClick(partner)"
							>
								<router-link
									:to="{
										name: 'exhibitor-by-id',
										params: {
											id: partner.partner_id,
										},
										query: {
											group: group_id,
										}
									}"
								>
									<img
										:src="partner.partner_logo"
										alt="partner_logo"
										loading="lazy"
									/>
								</router-link>
							</div>
						</div>

						<div
							class="event-card__show-btn"
							@click="handleShowAllExhibitors(group_name)"
						>
							<router-link
								:to="{
									name: 'partners',
									params: { id: group_id, name: group_name },
								}"
							>
								Show All
							</router-link>
						</div>
					</div>
					<div class="event-card__section" v-if="this.event.event_map_url || this.event.event_map">
						<v-card-text class="event-card__section-title"> Event Map</v-card-text>
						<a v-if="this.event.event_map" :href="this.event.event_map_url || this.event.event_map" target="_blank">
							<img

								:src="this.event.event_map"
								alt="event-map"
								loading="lazy"
								style="width: 100%"
							/>
						</a>

						<div class="iframe-container" v-else-if="this.event.event_map_url">
							<iframe
								:src="this.event.event_map_url"
								style="
                                width: 100%;
                                height: 400px;
                                border: none;"
								loading="lazy"
							></iframe>
						</div>
					</div>
					<div class="event-card__section" v-if="speakers.length">
						<v-card-text class="event-card__section-title"> Speaker(s)</v-card-text>
						<EventSpeakerCard
							v-for="speaker in speakers"
							:key="speaker.speaker_id"
							:speaker="speaker"
						/>
						<div class="event-card__show-btn">
							<router-link
								v-if="speakersShowAllButton"
								:to="{
									name: 'event-speakers',
									params: { id: this.event.event_id },
								}"
								@click.native="handleShowAllSpeakers"
							>
								Show All
							</router-link>
						</div>
					</div>
					<div v-if="polls.length" class="event-card__section">
						<div>
							<v-card-text class="event-card__section-title"> Survey(s)</v-card-text>
							<Poll
								v-for="poll in polls"
								:key="poll.poll_id"
								:poll="poll"
								:answers="polls_answers"
								:isEventVoteDirty="isVoteDirty"
								:pollsLoading="pollsLoading"
								@answerChange="answerChange"
							/>
							<v-btn
								v-if="!isPollsVoted"
								class="event-card__poll-btn"
								:class="{ 'event-card__poll-btn-disabled': voteDisabled }"
								:ripple="false"
								@click="handleVote"
								:loading="pollsLoading"
							>
								Vote
							</v-btn>
						</div>
					</div>
					<div class="event-card__section" v-if="ratings.length">
						<div>
							<v-card-text class="event-card__section-title"> Rating(s)</v-card-text>
							<Rating
								v-for="rating in ratings"
								:key="rating.rating_id"
								:rating="rating"
								@ratingChange="ratingChange"
							/>
							<v-btn
								v-if="isRatingsAvailable"
								class="event-card__rating-btn"
								:class="{ 'event-card__rating-btn-disabled': ratingDisabled }"
								:ripple="false"
								@click="handleRate"
								:loading="ratingsLoading"
							>
								Send Rating
							</v-btn>
						</div>
					</div>
				</div>
			</div>
		</div>
	</v-card>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import { mapActions, mapGetters } from 'vuex'
import Avatar from '@/components/Avatar'
import ActionPreview from '@/components/Event/Action/ActionPreview'
import Poll from '@/components/Post/Poll'
import Rating from '@/components/Event/Rating'
import EventSpeakerCard from '@/components/Event/EventSpeakerCard'
import Dwh from '@/mixins/Dwh'
import EventDescription from '@/components/Event/EventDescription.vue'
import Banner from '@/components/Banner.vue'

Vue.use(VueScrollTo)

export default {
	mixins: [Dwh],
	components: {
		Banner,
		EventDescription,
		ActionPreview,
		Avatar,
		Poll,
		EventSpeakerCard,
		Rating,
	},
	props: {
		event: {
			type: Object,
			default: () => {
			},
		},
	},
	data() {
		return {
			changeContent: false,
			offset: true,
			description: '',
			polls_answers: [],
			isVoteDirty: false,
			rating_answers: [],
		}
	},
	computed: {
		...mapGetters('auth', ['profile', 'isAuthenticated']),
		...mapGetters('event', [
			'isLoading',
			'attendees',
			'totalCountAttendees',
			'pollsLoading',
			'ratingsLoading',
		]),
		...mapGetters('advertisement', ['banner']),
		...mapGetters('settings', ['eventSettings']),

		isExternal() {
			return this.event.event_register_link.startsWith('http')
		},
		sponsorTextAnimationDuration() {
			if (this.event.event_sponsor_text) {
				const duration = this.event.event_sponsor_text.length / 20
				return duration < 5 ? 5 : duration
			} else {
				return null
			}
		},
		sponsorTextAnimation() {
			return this.event.event_sponsor_text.length <= 40 ? 'runningText1' : 'runningText2'
		},
		voteDisabled() {
			return (
				this.pollsLoading ||
				this.polls_answers.length !== this.polls.length ||
				this.polls_answers.some((poll) => poll.error)
			)
		},
		polls() {
			return this.event.polls.filter(
				(poll) =>
					poll.pollsAnswers.length ||
					(!poll.pollsAnswers.length && poll.allow_custom_answer),
			)
		},
		isPollsVoted() {
			return this.event.polls.every((poll) => poll.vote)
		},
		isAttendeesVisible() {
			if (this.eventSettings) {
				return this.eventSettings.show_attendees
			}
			return false
		},
		speakers() {
			return this.event.speakersList
		},
		speakersShowAllButton() {
			return this.event.speakersList.length >= 1
		},
		ratings() {
			return this.event.ratings.filter(
				(rating) => !rating.vote || (rating.vote && rating.display_rating),
			)
		},
		isRatingsAvailable() {
			return this.ratings.some((rating) => !rating.vote)
		},
		availableRatings() {
			return this.ratings.filter((rating) => !rating.vote)
		},
		ratingDisabled() {
			return this.rating_answers.length !== this.availableRatings.length
		},
	},
	methods: {
		...mapActions('advertisement', ['getBanner', 'clearBanner']),
		...mapActions('post', ['getPost', 'deletePost']),
		...mapActions('event', [
			'addEventToSchedule',
			'removeEventFromSchedule',
			'voteEventPolls',
			'voteEventRatings',
			'toggleIsFollowed',
		]),

		changeContentFunction() {
			this.changeContent = !this.changeContent
		},
		cutTextToSetCharacters(text, count) {
			if (!text || typeof text !== 'string') return ''
			if (text.length <= count) {
				return text
			} else {
				const parser = new DOMParser()
				const doc = parser.parseFromString(text, 'text/html')
				const truncatedHTML = doc.body.innerHTML.slice(0, count)
				return truncatedHTML + '...'
			}
		},
		getAddressLine(address) {
			return [address.address_city, address.address_state, address.address_country]
				.filter(function(v) {
					return v !== null
				})
				.join(',')
		},

		getAction(action) {
			let icons = {
				link: 'link',
				email: 'email',
				phone: 'phone',
				grid: 'phone_empty',
				fileList: 'document',
				linkList: 'list',
				deepLink: 'link',
				grid2: 'IconGrid2',
			}
			return {
				icon: icons[action.action_type],
				link:
					action.action_type !== '' && action.action_type !== ''
						? action.action_value
						: null,
				name: action.action_name,
				color: action.button_color,
			}
		},
		handleRegisterEvent() {
			this.sendTrigger({
				category: 'Content',
				action: 'click_register_button_event',
				module: 'Events',
				data: this.event.event_id,
				title: this.event.event_title,
				content_chapter_id: this.event.chapter_id ? [this.event.chapter_id] : null,
			})
		},
		handleShowAllExhibitors(title) {
			this.sendTrigger({
				category: 'Content',
				action: 'click_exhibitors_list_event',
				module: 'Events',
				data: title,
				content_chapter_id: this.event.chapter_id ? [this.event.chapter_id] : null,
			})
		},
		handleExhibitorClick(partner) {
			this.sendTrigger({
				category: 'Content',
				action: 'click_exhibitor_profile_event',
				module: 'Events',
				data: partner.partner_id,
				title: partner.partner_name,
				content_chapter_id: this.event.chapter_id ? [this.event.chapter_id] : null,
			})
		},
		handleClickAttendees() {
			this.sendTrigger({
				category: 'Content',
				action: 'click_attendees_list_event',
				module: 'Events',
				data: this.event.event_id,
				content_chapter_id: this.event.chapter_id ? [this.event.chapter_id] : null,
			})
		},
		handleClickAttendee(id) {
			this.sendTrigger({
				category: 'Content',
				action: 'click_attendee_profile_event',
				module: 'Events',
				data: id,
				content_chapter_id: this.event.chapter_id ? [this.event.chapter_id] : null,
			})
		},
		handleEventActionClick(eventAction) {
			let action = null
			switch (eventAction.action_type) {
				case 'email': {
					action = 'click_action_email_event'
					break
				}
				case 'phone': {
					action = 'click_action_phone_event'
					break
				}
				case 'link': {
					action = 'click_action_link_event'
					break
				}
				case 'deepLink': {
					action = 'click_action_link_event'
					break
				}
				case 'linkList': {
					action = 'click_action_list_of_links_event'
					break
				}
				case 'fileList': {
					action = 'click_action_list_of_files_event'
					break
				}
				default: {
					action = null
				}
			}
			if (action) {
				this.sendTrigger({
					category: 'Content',
					action,
					module: 'Events',
					title: eventAction.action_name,
					content_chapter_id: this.event.chapter_id ? [this.event.chapter_id] : null,
				})
			}
		},
		handleShowAllSpeakers() {
			this.sendTrigger({
				category: 'Content',
				action: 'click_speakers_list_event',
				module: 'Events',
				data: this.event.event_id,
				title: this.event.event_title,
				content_chapter_id: this.event.chapter_id ? [this.event.chapter_id] : null,
			})
		},
		answerChange(answer) {
			if (!answer.answer || (Array.isArray(answer.answer) && !answer.answer.length)) {
				if (this.polls_answers.length) {
					this.polls_answers = this.polls_answers.filter(
						(poll) => poll.poll_id !== answer.poll_id,
					)
				}
			} else {
				const pollAnswer = this.polls_answers.find(
					(poll) => poll.poll_id === answer.poll_id,
				)
				if (pollAnswer) {
					this.polls_answers = this.polls_answers.map((poll) => {
						if (poll.poll_id === answer.poll_id) {
							return answer
						} else {
							return poll
						}
					})
				} else {
					this.polls_answers = [...this.polls_answers, answer]
				}
			}
		},
		async handleVote() {
			this.isVoteDirty = true
			if (!this.voteDisabled) {
				await this.voteEventPolls({
					answers: this.polls_answers,
					eventId: this.event.event_id,
				})
				this.sendTrigger({
					category: 'Content',
					action: 'click_vote_poll_button_event',
					module: 'Polls',
					data: this.event.event_id,
					content_chapter_id: this.event.chapter_id ? [this.event.chapter_id] : null,
				})
			}
		},
		ratingChange(rating) {
			if (this.rating_answers.find((item) => item.rating_id === rating.rating_id)) {
				this.rating_answers = this.rating_answers.map((item) => {
					if (item.rating_id === rating.rating_id) {
						return {
							...rating,
						}
					} else {
						return item
					}
				})
			} else {
				this.rating_answers = [...this.rating_answers, rating]
			}
		},
		async handleRate() {
			if (!this.ratingDisabled) {
				await this.voteEventRatings({
					ratings: this.rating_answers,
					eventId: this.event.event_id,
				})
			}
		},
	},
	created() {
		if (this.$route.params.id !== undefined) {
			const bannerQuery = {
				places: ['event_general'],
				place_sub_id: this.$route.params.id,
			}
			this.getBanner(bannerQuery)
		}
		// parse html text to  save spaces and rows
		const parser = new DOMParser()
		const doc = parser.parseFromString(this.event.event_description, 'text/html')
		this.description = doc.body.innerHTML
	},
}
</script>

<style lang="scss">
.event-card {
	background-color: transparent !important;
	font-family: Montserrat, sans-serif;

	&__section {
		padding: 16px;
		background: #fff;
		margin-bottom: 8px;
		border-radius: 10px;

		&-title {
			font-size: 16px;
			font-weight: 700;
			line-height: 19.5px;
			color: #4a4a4a;
			margin-bottom: 12px;
		}
	}

	&__container {
		width: 100%;
		height: 100%;
		border-radius: 10px !important;
		margin-bottom: 30px;
	}

	&__content-inner {
		color: rgb(74, 74, 74);
		font-size: 15px;
		font-family: Montserrat, sans-serif;
		font-weight: normal;
		line-height: 18px;
		padding-top: 10px;

		img {
			width: 100%;
		}

		p {
			margin-bottom: 0;
		}

		h1 {
			font-size: 30px;
			padding-bottom: 8px;
			line-height: 30px;
		}
	}

	&__meta {
		font-family: Montserrat, sans-serif;
		font-style: normal;
		font-weight: bold;
		font-size: 18px;
		line-height: 20px;
		color: rgb(66, 160, 216);
		letter-spacing: 0.36px;
		gap: 4px;

		&-bottom {
			color: rgb(155, 155, 155);
			font-size: 16px;
			font-family: Montserrat, sans-serif;
			font-weight: normal;
			line-height: 16px;
		}

		&-time {
			color: rgb(155, 155, 155);
			font-size: 12px;
			font-family: Montserrat, sans-serif;
			font-weight: normal;
			text-align: right;
		}
	}

	&__list {
		position: relative;
		flex-direction: column;
	}

	&__title {
		color: rgb(74, 74, 74);
		font-size: 24px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;
	}

	&__event-time {
		color: rgb(74, 74, 74);
		font-size: 15px;
		font-family: Montserrat, sans-serif;
		font-weight: 600;
	}

	&__attendees {
		&-member {
			width: 50px;
			height: 50px;
			border-radius: 50%;
			object-fit: cover;
		}

		&-last-member {
			position: absolute;
			top: 0;
			bottom: 0;
			left: 0;
			right: 0;
			z-index: 999;
			background-color: rgba(0, 0, 0, 0.6);
			color: white;
			font-family: 'Montserrat', sans-serif;
			font-size: 14px;
			display: flex;
			justify-content: center;
			align-items: center;
			line-height: 3.7;

			width: 60px;
			height: 60px;
			border-radius: 50%;
		}

		&-last-container {
			position: relative !important;
			width: 60px !important;
		}
	}

	&__event {
		&-wrapper {
			display: flex;
			align-items: center;
			gap: 10px;
			width: 250px;
			margin-bottom: 20px;
			margin-right: 10px;

			i {
				border-radius: 50%;
				min-width: 40px;
				min-height: 40px;
			}
		}

		&-title {
			color: var(--ceil-detail-text-color);
			font-size: 16px;
			font-family: Montserrat, sans-serif;
			font-weight: normal;
		}
	}

	&__exhibitors {
		&-container {
			display: grid;
			grid-template-columns: repeat(3, 1fr);
			flex-wrap: wrap;
			align-items: center;
			padding: 10px 20px;
			gap: 16px;
			justify-content: space-between !important;

			@media screen and (max-width: 600px) {
				display: flex;
				flex-wrap: wrap;
				align-items: center;
				padding: 10px 20px;
				gap: 16px;
				justify-content: space-between !important;
			}
		}

		&-more {
			color: var(--accent-color);
			font-size: 14px;
			font-family: Montserrat, sans-serif;
			font-weight: bold;
			text-decoration: underline;
			text-align: right;
		}

		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;
		align-items: center;

		img {
			width: 120px;
		}
	}

	&__subtitle {
		&-category {
			color: rgb(155, 155, 155);
			font-size: 14px;
			font-family: Montserrat, sans-serif;
			font-weight: bold;
		}
	}

	&__registered {
		color: rgb(255, 255, 255) !important;
		font-size: 16px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;
		text-align: center;
		background-color: #a0a0a0 !important;
		border-radius: 8px;
		height: 50px !important;
		width: 100%;
	}

	&__register {
		color: rgb(255, 255, 255) !important;
		font-size: 16px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;
		text-align: center;
		background-color: var(--button-secondary-background-color) !important;
		border-radius: 8px;
		height: 50px !important;
		width: 100%;
	}

	&__action-wrapper {
		width: 40px;
		height: 40px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&_brief-description {
		font-family: 'Montserrat', sans-serif;
		font-size: 14px;
		color: #002855;
		line-height: 17.07px;
		font-weight: 400;
	}

	&__description {
		color: rgb(74, 74, 74);
		font-size: 15px;
		font-family: Montserrat, sans-serif;
		font-weight: normal;
		line-height: 22px;

		table {
			margin: 15px auto;
			border-collapse: collapse;
			font-family: Montserrat, sans-serif;
		}

		table td {
			padding: 15px;
			text-align: center;
		}

		table th {
			padding: 15px;
		}

		table thead td {
			background-color: #54585d;
			color: #ffffff;
			font-weight: bold;
			font-size: 13px;
			border: 1px solid #54585d;
			text-align: center;
		}

		table thead th {
			border: 1px solid #dddfe1;
		}

		table tbody td {
			color: #636363;
			border: 1px solid #dddfe1;
		}

		table tbody th {
			border: 1px solid #dddfe1;
		}

		table tbody tr {
			background-color: #f9fafb;
		}

		table tbody tr:nth-child(odd) {
			background-color: #ffffff;
		}

		img {
			width: 100%;
		}
	}

	&__tag {
		padding: 6px;
		border-radius: 3px !important;
		color: rgb(74, 74, 74);
		font-size: 14px !important;
		font-family: Montserrat, sans-serif;
		font-weight: normal;
		height: 20px !important;
		cursor: pointer;

		span {
			min-width: 100% !important;
			width: 100%;
		}
	}

	&__schedule-btn,
	&__show-btn {
		display: flex;
		justify-content: center;
		margin-top: 16px;

		a {
			color: var(--accent-color) !important;
			font-size: 16px;
			font-weight: 700;
			font-family: Montserrat, sans-serif;
		}
	}

	&__poll-btn,
	&__rating-btn {
		width: 100%;
		height: 50px !important;
		border-radius: 8px;
		font-size: 18px !important;
		line-height: 22px;
		font-weight: 700;
		text-transform: capitalize;
		background-color: var(--button-main-background-color) !important;
		color: var(--button-main-color) !important;

		&-disabled {
			opacity: 0.5;
			cursor: default;
		}
	}

	@media screen and (max-width: 1904px) {
		&__block {
			flex: 0 0 100%;
		}

		&__list {
			flex: 0 0 75%;
		}
	}

	@media screen and (max-width: 1264px) {
		&__block {
			flex: 0 0 100%;
		}

		&__list {
			flex: 0 0 66%;
		}
	}

	@media screen and (min-width: 1264px) {
		&__container {
			max-width: 600px;
		}
	}

	@media screen and (max-width: 768px) {
		&__thumbnail {
			height: 100%;
			max-width: 100%;
			border-radius: 10px;

			&-image {
				max-height: 250px;
			}
		}

		&__default-thumbnail {
			height: 100%;
		}

		&__outer {
			flex-direction: column;
		}
	}
}

@keyframes runningText1 {
	// 0 - 40
	0% {
		transform: translateX(100%);
	}

	100% {
		transform: translateX(-50%);
	}
}

@keyframes runningText2 {
	// 50 - 180
	0% {
		transform: translateX(100%);
	}

	100% {
		transform: translateX(-100%);
	}
}

</style>
